/**
 * Convert date block conditions to Formly hideExpression
 * Handles: date, date-month blocks
 */

export interface DateConditionProps {
	mode: 'equal' | 'before' | 'after' | 'between' | 'defined' | 'undefined';
	value?: number;
	to?: number;
}

/**
 * Convert a date condition to hideExpression
 * Returns expression that evaluates to TRUE when condition is MET
 * (Remember: hideExpression hides when TRUE, so we'll invert later)
 *
 * Note: Tripetto stores dates as Unix timestamps (milliseconds since epoch)
 * Formly date fields also use timestamp values for comparison
 */
export function convertDateCondition(fieldKey: string, props: DateConditionProps): string | undefined {
	const { mode, value, to } = props;

	switch (mode) {
		case 'equal':
			// Date must equal the specified date (comparing timestamps)
			if (value === undefined || value === null) {
				return undefined;
			}
			// Compare date values (timestamps)
			return `model['${fieldKey}'] === ${value}`;

		case 'before':
			// Date must be before the specified date
			if (value === undefined || value === null) {
				return undefined;
			}
			return `model['${fieldKey}'] < ${value}`;

		case 'after':
			// Date must be after the specified date
			if (value === undefined || value === null) {
				return undefined;
			}
			return `model['${fieldKey}'] > ${value}`;

		case 'between':
			// Date must be between two dates (inclusive)
			if (value === undefined || value === null || to === undefined || to === null) {
				return undefined;
			}
			return `(model['${fieldKey}'] >= ${value} && model['${fieldKey}'] <= ${to})`;

		case 'defined':
			// Field must have a date value
			return `(model['${fieldKey}'] !== null && model['${fieldKey}'] !== undefined && model['${fieldKey}'] !== '')`;

		case 'undefined':
			// Field must be empty, null, or undefined
			return `(model['${fieldKey}'] === null || model['${fieldKey}'] === undefined || model['${fieldKey}'] === '')`;

		default:
			// Unknown mode
			return undefined;
	}
}
